import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { marketService } from '../lib/services/marketService';
import type { MarketListing, MarketFilters } from '../types';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';
import FilterForm from '../components/market/FilterForm';
import ListingForm from '../components/market/ListingForm';
import ListingCard from '../components/market/ListingCard';
import { useTheme } from '../contexts/ThemeContext';
import { Spinner } from '@/components/ui/Spinner';

// Imports do MUI que ainda podem ser usados temporariamente ou por subcomponentes não refatorados
import {
  Alert as MuiAlert,
  Pagination as MuiPagination,
  Chip as MuiChip, 
  Button as MuiButton, // Mantido para qualquer botão MUI restante, se houver
  useTheme as useMuiTheme // Hook de tema do MUI
} from '@mui/material';
// Ícones do MUI (podem ser substituídos por lucide-react gradualmente)
import { Add, FilterList, Sell, ShoppingCart, Delete, Edit, Check, InfoOutlined, Visibility, CheckCircle, KeyboardArrowUp, KeyboardArrowDown, LocationOn } from '@mui/icons-material';
import { PlusCircle, ListFilter } from 'lucide-react'; // Ícones Lucide equivalentes

// Componentes de UI baseados em Tailwind (shadcn/ui)
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button"; // Botão principal de shadcn/ui

const ITEMS_PER_PAGE = 12;

const MarketPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { theme: currentTheme } = useTheme();
  const muiThemeHook = useMuiTheme(); // Renomeado para evitar confusão com a variável muiTheme nos ListingCards antigos

  const [listings, setListings] = useState<MarketListing[]>([]);
  const [userListings, setUserListings] = useState<MarketListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [userListingsLoading, setUserListingsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userError, setUserError] = useState<string | null>(null);
  
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [totalUserItems, setTotalUserItems] = useState(0);

  const [filters, setFilters] = useState<MarketFilters>(() => {
    const newInitialFilters: Partial<MarketFilters> = {};
    const knownNonFilterParams = ['tab', 'showFilters']; // Lista de parâmetros de URL que não são filtros

    searchParams.forEach((value, keyString) => {
      if (knownNonFilterParams.includes(keyString)) {
        return; // Ignorar 'tab' e 'showFilters'
      }

      if (keyString === 'searchTerm') {
        newInitialFilters.search_text = value;
        return; // Continue to next searchParam
      }

      const key = keyString as keyof MarketFilters;

      switch (key) {
        case 'min_price': {
          const val = parseInt(value, 10);
          if (!isNaN(val) && val !== 0) { // Só define se não for NaN e não for 0 (valor padrão "sem filtro inferior")
            newInitialFilters.min_price = val;
          }
          break;
        }
        case 'max_price': {
          const val = parseInt(value, 10);
          if (!isNaN(val) && val !== 10000) { // Só define se não for NaN e não for 10000 (valor padrão "sem filtro superior")
            newInitialFilters.max_price = val;
          }
          break;
        }
        case 'max_players':
        case 'min_players':
        case 'min_playtime':
        case 'max_playtime':
        case 'min_age':
          (newInitialFilters[key] as number | undefined) = parseInt(value, 10);
          break;
        case 'trade':
        case 'include_sold':
          (newInitialFilters[key] as boolean | undefined) = value === 'true';
          break;
        case 'condition':
          if (value) newInitialFilters.condition = value.split(',') as Array<'new' | 'like_new' | 'good' | 'fair' | 'poor'>;
          break;
        case 'categories':
          if (value) newInitialFilters.categories = value.split(',');
          break;
        case 'mechanics':
          if (value) newInitialFilters.mechanics = value.split(',');
          break;
        case 'listing_type':
          if (value === 'sell' || value === 'buy' || value === 'trade') {
            newInitialFilters.listing_type = value;
          }
          break;
        case 'status':
          if (value === 'active' || value === 'completed' || value === 'canceled') {
            newInitialFilters.status = value;
          }
          break;
        case 'city':
        case 'state':
        case 'country':
        case 'location': // Adicionado location aqui para ser tratado como string
          newInitialFilters[key] = value; 
          break;
        case 'sort_by':
          if (value === 'price' || value === 'created_at' || value === 'relevance') {
            newInitialFilters.sort_by = value;
    } else {
            console.warn(`Invalid sort_by value from URL: ${value}`);
          }
          break;
        case 'order':
          if (value === 'asc' || value === 'desc') {
            newInitialFilters.order = value;
          }
          break;
        default:
          console.warn(`Unhandled or unexpected (and not explicitly ignored) filter key from URL: ${keyString}`);
      }
    });
    return newInitialFilters as MarketFilters;
  });

  const [showFilters, setShowFilters] = useState(Object.keys(filters).length > 0 || !!searchParams.get('showFilters'));
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || "all-listings");
  
  const [selectedListing, setSelectedListing] = useState<MarketListing | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hideCompletedListings, setHideCompletedListings] = useState(true);
  const [hideOwnListings, setHideOwnListings] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab) {
      params.set('tab', activeTab);
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        const paramKey = key === 'search_text' ? 'searchTerm' : key;
        if (Array.isArray(value)) {
          params.set(paramKey, value.join(','));
      } else {
          params.set(paramKey, String(value));
        }
        } else {
        const paramKey = key === 'search_text' ? 'searchTerm' : key;
        params.delete(paramKey);
      }
    });
    if (showFilters) params.set('showFilters', 'true'); else params.delete('showFilters');

    const timer = setTimeout(() => {
      navigate(`?${params.toString()}`, { replace: true });
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, navigate, showFilters, activeTab]);

  const loadListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const effectiveFilters = { ...filters };
      if (user && hideOwnListings && activeTab === 'all-listings') {
        effectiveFilters.exclude_user_id = user.id;
      }
      console.log('[MarketPage] Loading ALL listings with effectiveFilters:', effectiveFilters, 'Page:', page);
      const { data, count, error: serviceError } = await marketService.getListings(effectiveFilters, ITEMS_PER_PAGE, (page - 1) * ITEMS_PER_PAGE);
      console.log('[MarketPage] ALL listings response - Data:', data, 'Count:', count, 'Error:', serviceError);
      if (serviceError) throw serviceError;
      setListings(data || []);
      setTotalItems(count || 0);
    } catch (err: any) {
      console.error("[MarketPage] Error loading listings:", err);
      setError(err.message || "Failed to load listings.");
      setListings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const loadUserListings = async () => {
    if (!user) return;
    setUserListingsLoading(true);
    setUserError(null);
    try {
      const effectiveFilters: MarketFilters = {
         ...filters,
         user_id: user.id,
         include_sold: !hideCompletedListings,
       };
      console.log('[MarketPage] Loading USER listings with effectiveFilters:', effectiveFilters, 'UserPage:', userPage);
      const { data, count, error: serviceError } = await marketService.getListings(effectiveFilters, ITEMS_PER_PAGE, (userPage - 1) * ITEMS_PER_PAGE);
      console.log('[MarketPage] USER listings response - Data:', data, 'Count:', count, 'Error:', serviceError);
      if (serviceError) throw serviceError;
      setUserListings(data || []);
      setTotalUserItems(count || 0);
    } catch (err: any) {
      console.error("[MarketPage] Error loading user listings:", err);
      setUserError(err.message || "Failed to load your listings.");
      setUserListings([]);
      setTotalUserItems(0);
    } finally {
      setUserListingsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "all-listings") {
      loadListings();
    }
  }, [page, filters, activeTab, user, hideOwnListings]);

  useEffect(() => {
    if (user && activeTab === "my-listings") {
      loadUserListings();
    }
  }, [user, userPage, filters, activeTab, hideCompletedListings]);

  const handleFilterChange = (newFilters: MarketFilters) => {
    setPage(1);
    setUserPage(1);
    setFilters(newFilters);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleUserPageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setUserPage(value);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
      setPage(1);
      setUserPage(1);
    setError(null);
    setUserError(null);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/market/${id}`);
  };

  const handleEditListing = (listing: MarketListing) => {
    setSelectedListing(listing);
    setIsEditMode(true);
    setFormOpen(true);
  };

  const handleDeleteListing = async (id: string) => {
    if (!user) {
      alert("Você precisa estar logado para deletar anúncios.");
      return;
    }
    if (window.confirm("Tem certeza que deseja deletar este anúncio?")) {
      try {
        const { error: deleteError } = await marketService.deleteListing(id, user.id, user.is_admin || false);
        if (deleteError) throw deleteError;
        alert("Anúncio deletado com sucesso!");
        if (activeTab === "all-listings") {
            loadListings();
        } else if (activeTab === "my-listings") {
            loadUserListings();
        }
      } catch (err: any) {
        console.error("Error deleting listing:", err);
        alert(`Erro ao deletar anúncio: ${err.message}`);
      }
    }
  };

  const handleMarkAsCompleted = async (id: string) => {
    if (!user) {
      alert("Você precisa estar logado para gerenciar anúncios.");
      return;
    }
    if (window.confirm("Tem certeza que deseja marcar este anúncio como concluído?")) {
      try {
        const { error: updateError } = await marketService.markListingAsCompleted(id, user.id);
        if (updateError) throw updateError;
        alert("Anúncio marcado como concluído!");
        if (activeTab === "my-listings") {
            loadUserListings();
          } else {
            loadListings();
          }
      } catch (err: any) {
        console.error("Error marking listing as completed:", err);
        alert(`Erro ao marcar anúncio como concluído: ${err.message}`);
      }
    }
  };

  const handleNewListing = () => {
    setSelectedListing(null);
    setIsEditMode(false);
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData: Omit<MarketListing, 'id' | 'created_at' | 'updated_at' | 'user_profile' | 'game'>) => {
    if (!user) {
      alert("Você precisa estar logado para criar ou editar anúncios.");
      return;
    }
    setLoading(true);
    try {
      const {
        ...restOfFormData
      } = formData;

      const listingPayload = {
        ...restOfFormData,
        user_id: user.id,
        price: formData.price ? parseFloat(String(formData.price)) : null,
      };

      let success = false;
      let errorMsg: string | null = null;

      if (isEditMode && selectedListing) {
        const { 
          user_id: payload_user_id,
          game_id: payload_game_id,
          ...updateData
        } = listingPayload;
        
        const result = await marketService.updateListing(selectedListing.id, user.id, updateData);
        success = result.success;
        if (result.error) errorMsg = result.error;
      } else {
        if (!listingPayload.game_id || !listingPayload.listing_type) {
          throw new Error("Game ID e Tipo de Anúncio são obrigatórios para criar um novo anúncio.");
        }
        const result = await marketService.createListing(
          user.id,
          listingPayload.game_id!,
          listingPayload.listing_type!,
          listingPayload.price,
          listingPayload.condition,
          listingPayload.description,
          listingPayload.location,
          listingPayload.city,
          listingPayload.state,
          listingPayload.country
        );
        success = result.success;
        if (result.error) errorMsg = result.error;
      }

      if (!success) {
        throw new Error(errorMsg || "Falha ao salvar o anúncio.");
      }

      alert(isEditMode ? "Anúncio atualizado com sucesso!" : "Anúncio criado com sucesso!");
      setFormOpen(false);
            loadListings();
      if (user) loadUserListings();

    } catch (err: any) {
      console.error("Error submitting form:", err);
      alert(`Erro ao salvar anúncio: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  const bgColor = currentTheme === 'dark' ? 'bg-gray-900' : 'bg-gray-50';
  const textColor = currentTheme === 'dark' ? 'text-gray-100' : 'text-gray-900';
  const cardBgColor = currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const borderColor = currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200';

  return (
    <>
      <SEO 
        title="Mercado de Jogos"
        description="Encontre jogos de tabuleiro para comprar ou vender na comunidade."
      />
      <div className={`min-h-screen ${bgColor} ${textColor} py-4 px-2 sm:px-6 lg:px-8`}>
        <header className="mb-6">
          <h1 className={`text-3xl font-bold ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Mercado de Jogos de Tabuleiro
          </h1>
          <p className={`mt-1 text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Anuncie, compre, venda ou troque jogos com outros membros da comunidade.
          </p>
        </header>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className={`grid w-full ${user ? 'grid-cols-2' : 'grid-cols-1'} mb-4 p-0 rounded-lg ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <TabsTrigger 
              value="all-listings"
              className={`px-3 py-2 text-sm font-medium transition-colors border-0
                          ${currentTheme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-300'}
                          data-[state=active]:!${currentTheme === 'dark' ? 'bg-purple-700 text-white' : 'bg-green-500 text-white'}`}
            >
              Todos os Anúncios
            </TabsTrigger>
            {user && (
              <TabsTrigger 
                value="my-listings"
                className={`px-3 py-2 text-sm font-medium transition-colors border-0
                            ${currentTheme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-300'}
                            data-[state=active]:!${currentTheme === 'dark' ? 'bg-purple-700 text-white' : 'bg-green-500 text-white'}`}
              >
                Meus Anúncios
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="all-listings">
            <div className={`p-4 rounded-lg ${cardBgColor} border ${borderColor} shadow-sm`}>
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                <div className="flex flex-wrap items-center gap-2">
                <Button 
                    variant={showFilters ? "default" : "outline"}
                  onClick={toggleFilters}
                    className={`${showFilters && (currentTheme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white')}`}
                  >
                    <ListFilter className="mr-2 h-4 w-4" />
                    {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                </Button>
                  {user && (
                     <Button 
                       variant={hideOwnListings ? "outline" : "secondary"} 
                       size="sm"
                       onClick={() => setHideOwnListings(!hideOwnListings)}
                       className={`text-xs px-2 py-1 h-auto ${hideOwnListings ? (currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100') 
                                                                         : (currentTheme === 'dark' ? 'bg-sky-700 text-sky-100 hover:bg-sky-800' : 'bg-sky-500 text-white hover:bg-sky-600')}`}
                     >
                       {hideOwnListings ? "Mostrar Meus Anúncios" : "Ocultar Meus Anúncios"}
                     </Button>
                  )}
                </div>
              
              {user && (
                <Button 
                  onClick={handleNewListing}
                    className={`${currentTheme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                  Novo Anúncio
                </Button>
              )}
              </div>
            
              <div className="grid grid-cols-12 gap-6">
              {showFilters && (
                  <div className="col-span-12 md:col-span-3">
                  <FilterForm 
                    filters={filters} 
                    onFilterChange={handleFilterChange} 
                  />
                  </div>
                )}
                
                <div className={`col-span-12 ${showFilters ? 'md:col-span-9' : 'md:col-span-12'}`}>
                {error && (
                    <MuiAlert severity="error" sx={{ mb: 3, bgcolor: currentTheme === 'dark' ? muiThemeHook.palette.error.dark : muiThemeHook.palette.error.light, color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : muiThemeHook.palette.error.dark }}>
                    {error}
                    </MuiAlert>
                )}
                
                {loading ? (
                    <div className="flex justify-center p-4">
                      <Spinner />
                    </div>
                ) : listings.length === 0 ? (
                    <MuiAlert severity="info" sx={{ bgcolor: currentTheme === 'dark' ? muiThemeHook.palette.info.dark : muiThemeHook.palette.info.light, color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : muiThemeHook.palette.info.dark }}>
                    Nenhum anúncio encontrado com os filtros aplicados.
                    </MuiAlert>
                ) : (
                  <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {listings.map((listing) => (
                          <ListingCard
                            key={listing.id}
                            listing={listing}
                            onViewDetails={handleViewDetails}
                            onEdit={handleEditListing}
                            onDelete={handleDeleteListing}
                            onMarkAsCompleted={handleMarkAsCompleted}
                            showUserActions={false}
                            isAdmin={user?.is_admin}
                          />
                        ))}
                      </div>
                      
                      {totalItems > ITEMS_PER_PAGE && (
                        <div className="flex justify-center mt-6">
                          <MuiPagination
                            count={Math.ceil(totalItems / ITEMS_PER_PAGE)} 
                          page={page} 
                          onChange={handlePageChange} 
                          color="primary" 
                            sx={{ '& .MuiPaginationItem-root': { color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : undefined } }} 
                        />
                        </div>
                    )}
                  </>
                )}
                </div>
              </div>
            </div>
          </TabsContent>
          
          {user && (
            <TabsContent value="my-listings">
              <div className={`p-4 rounded-lg ${cardBgColor} border ${borderColor} shadow-sm`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                    <Button 
                      variant={hideCompletedListings ? "outline" : "secondary"} 
                      size="sm"
                      onClick={() => setHideCompletedListings(!hideCompletedListings)}
                      className={`text-xs px-2 py-1 h-auto ${hideCompletedListings ? (currentTheme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100') 
                                                                             : (currentTheme === 'dark' ? 'bg-sky-700 text-sky-100 hover:bg-sky-800' : 'bg-sky-500 text-white hover:bg-sky-600')}`}
                    >
                      {hideCompletedListings ? "Mostrar Concluídos" : "Ocultar Concluídos"}
                    </Button>
              
              <Button 
                onClick={handleNewListing}
                    className={`${currentTheme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
              >
                    <PlusCircle className="mr-2 h-4 w-4" />
                Novo Anúncio
              </Button>
                </div>
            
                <div className="grid grid-cols-1">
                  <div> 
                {userError && (
                      <MuiAlert severity="error" sx={{ mb: 3, bgcolor: currentTheme === 'dark' ? muiThemeHook.palette.error.dark : muiThemeHook.palette.error.light, color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : muiThemeHook.palette.error.dark }}>
                    {userError}
                      </MuiAlert>
                )}
                
                {userListingsLoading ? (
                      <div className="flex justify-center p-4">
                        <Spinner />
                      </div>
                ) : userListings.length === 0 ? (
                      <MuiAlert severity="info" sx={{ mb: 3, bgcolor: currentTheme === 'dark' ? muiThemeHook.palette.info.dark : muiThemeHook.palette.info.light, color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : muiThemeHook.palette.info.dark }}>
                        Você não possui anúncios {hideCompletedListings ? 'ativos' : ''}. Crie um novo anúncio clicando no botão acima.
                      </MuiAlert>
                ) : (
                  <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userListings.map((listing) => (
                          <ListingCard
                              key={listing.id}
                            listing={listing}
                            onViewDetails={handleViewDetails}
                            onEdit={handleEditListing}
                            onDelete={handleDeleteListing}
                            onMarkAsCompleted={handleMarkAsCompleted}
                            showUserActions={true}
                            isAdmin={user?.is_admin}
                          />
                          ))}
                        </div>
                        
                        {totalUserItems > ITEMS_PER_PAGE && (
                          <div className="flex justify-center mt-6">
                            <MuiPagination
                              count={Math.ceil(totalUserItems / ITEMS_PER_PAGE)} 
                          page={userPage} 
                          onChange={handleUserPageChange} 
                          color="primary" 
                              sx={{ '& .MuiPaginationItem-root': { color: currentTheme === 'dark' ? muiThemeHook.palette.text.primary : undefined } }}
                        />
                          </div>
                    )}
                  </>
                )}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
        
        {formOpen && (
        <ListingForm 
            listing={selectedListing || undefined}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
        )}
      </div>
    </>
  );
};

export default MarketPage; 